import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useEditMode } from '../../context/EditModeContext';
import { useContent } from '../../context/ContentContext';
import { useRichTextTarget } from '../../hooks/useRichTextTarget';
import { hasRichHtml, sanitizeRichHtml, getEditablePlainText, normalizeStoredText } from '../../utils/richText';
import './editable.css';

function styleObjectToString(style) {
  if (!style || typeof style !== 'object') return '';
  return Object.entries(style)
    .map(([key, value]) => `${key.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`)}: ${value}`)
    .join('; ');
}

function reactNodeToHtml(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(reactNodeToHtml).join('');

  if (!React.isValidElement(node)) return '';

  if (node.type === 'br') return '<br />';

  const tagName = typeof node.type === 'string' ? node.type : 'span';
  const { style, children } = node.props || {};
  const styleAttr = style ? ` style="${styleObjectToString(style)}"` : '';
  const innerHtml = reactNodeToHtml(children);

  if (tagName === 'br') return '<br />';
  return `<${tagName}${styleAttr}>${innerHtml}</${tagName}>`;
}

function getDefaultContent(children, html) {
  if (html) {
    if (typeof children === 'string') return children;
    return reactNodeToHtml(children);
  }
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) {
    return children
      .map((child) => (typeof child === 'string' ? child : child?.props?.children ?? ''))
      .join('');
  }
  return typeof children === 'string' ? children : '';
}

function resolveDisplayValue(rawValue, fallback) {
  if (rawValue === undefined || rawValue === null) return fallback;
  if (typeof rawValue === 'string') {
    if (rawValue.includes('[object Object]')) return fallback;
    if (rawValue.trim() === '') return fallback;
    return normalizeStoredText(rawValue);
  }
  return rawValue;
}

function placeCaretAtEnd(element) {
  if (!element) return;
  const range = document.createRange();
  const selection = window.getSelection();
  range.selectNodeContents(element);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
}

function shouldRenderAsHtml(value, html) {
  return Boolean(html) || hasRichHtml(value);
}

export default function EditableText({
  contentKey,
  tag: Tag = 'span',
  className = '',
  html = false,
  multiline = false,
  children,
  ...rest
}) {
  const { isEditing } = useAuth();
  const { controlsVisible, activeFieldKey, clearActiveField } = useEditMode();
  const { getContent, setContentValue, clearContentValue } = useContent();
  const ref = useRef(null);
  const isActiveRef = useRef(false);
  const [isActive, setIsActive] = useState(false);

  const fallback = getDefaultContent(children, html);
  const displayValue = resolveDisplayValue(getContent(contentKey, fallback), fallback);
  const isPanelFocused = activeFieldKey === contentKey;
  const renderAsHtml = shouldRenderAsHtml(displayValue, html);

  useRichTextTarget(isActive, ref.current);

  const syncDomFromValue = useCallback((value) => {
    const resolved = resolveDisplayValue(value, fallback);
    if (!ref.current || isActiveRef.current) return;

    if (renderAsHtml || html) {
      const nextHtml = renderAsHtml ? resolved : resolved;
      if (ref.current.innerHTML !== nextHtml) {
        ref.current.innerHTML = nextHtml;
      }
      return;
    }

    if (ref.current.textContent !== resolved) {
      ref.current.textContent = resolved;
    }
  }, [html, fallback, renderAsHtml]);

  const setRef = useCallback(
    (node) => {
      ref.current = node;
      if (node && isEditing && !isActiveRef.current) {
        if (renderAsHtml || html) node.innerHTML = displayValue;
        else node.textContent = displayValue;
      }
    },
    [displayValue, html, isEditing, renderAsHtml]
  );

  useLayoutEffect(() => {
    if (!isEditing) return;
    syncDomFromValue(displayValue);
  }, [displayValue, syncDomFromValue, isEditing]);

  const save = useCallback(() => {
    if (!ref.current || !isActiveRef.current) return;

    const rawHtml = sanitizeRichHtml(ref.current.innerHTML);
    const plainText = getEditablePlainText(ref.current);
    const isEmpty = plainText === '';

    isActiveRef.current = false;
    setIsActive(false);
    clearActiveField();

    if (isEmpty) {
      clearContentValue(contentKey);
      if (html || renderAsHtml) ref.current.innerHTML = fallback;
      else ref.current.textContent = fallback;
      return;
    }

    const valueToSave = hasRichHtml(rawHtml) || html ? rawHtml : plainText;
    setContentValue(contentKey, valueToSave);
  }, [contentKey, html, fallback, renderAsHtml, setContentValue, clearContentValue, clearActiveField]);

  useEffect(() => {
    if (!isPanelFocused || isActiveRef.current || !ref.current) return;
    syncDomFromValue(displayValue);
    isActiveRef.current = true;
    setIsActive(true);
    ref.current.focus();
    placeCaretAtEnd(ref.current);
  }, [isPanelFocused, displayValue, syncDomFromValue]);

  const handleFocus = useCallback(() => {
    isActiveRef.current = true;
    setIsActive(true);
  }, []);

  const handleBlur = useCallback(() => {
    window.setTimeout(() => {
      if (isActiveRef.current) {
        save();
      }
    }, 0);
  }, [save]);

  const handleKeyDown = useCallback((e) => {
    if (!multiline && e.key === 'Enter') {
      e.preventDefault();
      ref.current?.blur();
    }
    if (e.key === 'Escape') {
      syncDomFromValue(displayValue);
      isActiveRef.current = false;
      setIsActive(false);
      clearActiveField();
      ref.current?.blur();
    }
  }, [displayValue, multiline, syncDomFromValue, clearActiveField]);

  if (!isEditing) {
    if (renderAsHtml) {
      return (
        <Tag
          className={`rich-text-content ${className}`.trim()}
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(displayValue) }}
          {...rest}
        />
      );
    }
    return (
      <Tag className={className} {...rest}>
        {displayValue}
      </Tag>
    );
  }

  return (
    <Tag
      ref={setRef}
      className={`editable-text rich-text-content ${className} ${isActive ? 'editable-text--active' : ''}${isPanelFocused ? ' editable-field--focused' : ''}${!controlsVisible ? ' editable-text--subtle' : ''}`}
      contentEditable
      suppressContentEditableWarning
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      data-content-key={contentKey}
      title="Click to edit — use the formatting toolbar for bold, color, links"
      {...rest}
    />
  );
}
